/* Subtasks (2026-10-30, Duncan: "can we show something visual there that lets them know that it makes a sub task on the
   schedule?"): where a subtask goes, in Add Task and Task Properties -- its task and that task's subtasks as the Schedule
   lists them, it among them by start date, and the task's dates as they'll be with it. */
import React from 'react';

const fmt = (d) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '–';
};
const spanOf = (list, fallback) => {
  const starts = list.map((r) => r.start_date).filter(Boolean);
  const ends = list.map((r) => r.finish_date || r.start_date).filter(Boolean);
  return {
    start: starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : fallback.start_date,
    finish: ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : fallback.finish_date,
  };
};
const elbow = (color) => (
  <span aria-hidden style={{ display: 'inline-block', width: 9, height: 8, borderLeft: `1.5px solid ${color}`, borderBottom: `1.5px solid ${color}`, borderBottomLeftRadius: 3, margin: '0 7px 3px 2px', flexShrink: 0 }} />
);

/* (Duncan 2026-10-30, the arrows decision: arrows on bottom rows only, plus two checks when a subtask comes
   later): what the box offers about arrows. (1) A new subtask with no arrow yet, whose first sisters -- the ones no sister
   arrow starts -- wait on tasks outside: wait for them too? (2) A subtask that now finishes after the one a task outside
   waits on: move that arrow to it? Each one a checkbox, ticked to start (a later start is safer than an early one).
   10-31 (Duncan: "how does the project manager know that when they're creating the task?"): each one says what it does
   to the dates, and the subtask's row in the box shows the result, ticked or not. */
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
export function arrowOffers({ parent, siblings, me, myPreds = [], allTasks = [], isNew = true }) {
  if (!parent) return [];
  const sisters = siblings.filter((s) => s.id !== me.id);
  const family = new Set([...sisters.map((s) => s.id), me.id, parent.id]);
  const nameOf = (id) => (allTasks.find((t) => t.id === id) || {}).task_name || 'a task';
  const meName = me.task_name || 'this subtask';
  const out = [];
  const firsts = sisters.filter((s) => !(s.predecessors || []).some((p) => family.has(Number(p.predecessor_task_id))));
  const firstEnd = firsts.map((s) => s.finish_date).filter(Boolean).sort().pop();
  // Only for a subtask that starts alongside the first steps -- one that comes at the end (Final cleanup) follows a sister,
  // and waiting on what the first steps wait for would be noise.
  if (isNew && !myPreds.length && (!me.start_date || !firstEnd || me.start_date <= firstEnd)) {
    const seen = new Set();
    firsts.forEach((s) => (s.predecessors || []).forEach((p) => {
      const id = Number(p.predecessor_task_id);
      if (family.has(id) || seen.has(id)) return;
      seen.add(id);
      out.push({ key: `wait:${id}`, kind: 'wait', predId: id, predName: nameOf(id),
        text: `Hold ${meName} until ${nameOf(id)} is done, like ${s.task_name}. If ${nameOf(id)} runs late, ${meName} moves with it.` });
    }));
  }
  const last = sisters.filter((s) => s.finish_date).sort((a, b) => b.finish_date.localeCompare(a.finish_date))[0];
  const myFinish = me.finish_date || me.start_date;
  if (last && myFinish && myFinish > last.finish_date) {
    allTasks.filter((t) => !family.has(t.id)).forEach((t) => (t.predecessors || []).forEach((p) => {
      if (Number(p.predecessor_task_id) !== last.id) return;
      out.push({ key: `move:${t.id}:${p.id}`, kind: 'move', taskId: t.id, taskName: t.task_name, link: p, fromId: last.id,
        text: `Hold ${t.task_name} until ${meName} is done, not ${last.task_name}. ${cap(meName)} now finishes last. If it runs late, ${t.task_name} moves with it.` });
    }));
  }
  return out;
}

/* A task's first subtask takes its arrows (the spec: the arrow into the task moves down, and the arrow out of it comes
   from its last subtask -- with one subtask, that one; the server's subtasks.js moveArrowsDown). What the new subtask
   will wait for, and what will wait for it, shown under its row as the checks' results are. */
export function handedArrows({ parent, siblings, me, allTasks = [] }) {
  if (!parent || siblings.some((s) => s.id !== me.id)) return { waits: [], heldBy: [] };
  const nameOf = (id) => (allTasks.find((t) => t.id === id) || {}).task_name || 'a task';
  const waits = (parent.predecessors || []).map((p) => Number(p.predecessor_task_id)).filter((id) => id !== me.id).map(nameOf);
  const heldBy = allTasks.filter((t) => t.id !== me.id && t.id !== parent.id
    && (t.predecessors || []).some((p) => Number(p.predecessor_task_id) === parent.id)).map((t) => t.task_name);
  return { waits, heldBy };
}

// The lines under the subtask's row: what each checkbox does to it, ticked (green or purple, like the list's arrows) or
// not (amber).
const arrowSvg = { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
const NOTE_ICON = {
  ok: <svg {...arrowSvg} stroke="#10b981"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>,
  dep: <svg {...arrowSvg} stroke="#8b5cf6"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>,
  warn: <svg {...arrowSvg} stroke="currentColor" strokeWidth={2.2}><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h16.9a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
};
const NOTE_COLOR = { ok: 'var(--success)', dep: '#7c3aed', warn: 'var(--warning)' };

export default function SubtaskPlacement({ parent, siblings, me, meLabel = 'New', offers = [], picks = {}, onPick, handed = null }) {
  if (!parent) return null;
  const on = (o) => picks[o.key] !== false;
  const waits = offers.filter((o) => o.kind === 'wait');
  const moves = offers.filter((o) => o.kind === 'move');
  const notes = [];
  if (handed && handed.waits.length) notes.push({ key: 'handed-wait', tone: 'ok', text: `Waits for ${handed.waits.join(', ')}, as ${parent.task_name} did` });
  if (handed && handed.heldBy.length) notes.push({ key: 'handed-held', tone: 'dep', text: `${handed.heldBy.join(', ')} ${handed.heldBy.length > 1 ? 'wait' : 'waits'} for it now` });
  if (waits.some(on)) notes.push({ key: 'wait', tone: 'ok', text: `Waits for ${waits.filter(on).map((o) => o.predName).join(', ')}` });
  waits.filter((o) => !on(o)).forEach((o) => notes.push({ key: o.key, tone: 'warn',
    text: me.start_date ? `Starts ${fmt(me.start_date)}, even if ${o.predName} runs late` : `Won't wait for ${o.predName}` }));
  const held = moves.filter(on).map((o) => o.taskName);
  if (held.length) notes.push({ key: 'move', tone: 'dep', text: `${held.join(', ')} ${held.length > 1 ? 'wait' : 'waits'} for it` });
  moves.filter((o) => !on(o)).forEach((o) => notes.push({ key: o.key, tone: 'warn', text: `${o.taskName} could start before it's done` }));
  const others = siblings.filter((s) => s.id !== me.id);
  const rows = [...others, { ...me, _me: true }]
    .sort((a, b) => (a.start_date || '9999').localeCompare(b.start_date || '9999') || (a._me ? 1 : 0) - (b._me ? 1 : 0));
  const was = others.length ? spanOf(others, parent) : { start: parent.start_date, finish: parent.finish_date };
  const now = spanOf(rows, parent);
  const at = rows.findIndex((r) => r._me);
  const from = rows.length > 7 ? Math.max(0, at - 2) : 0;
  const to = rows.length > 7 ? Math.min(rows.length, at + 3) : rows.length;
  const grid = { display: 'grid', gridTemplateColumns: '14px 76px minmax(0,1fr) 118px', columnGap: 8, alignItems: 'center', fontSize: 13.5, padding: '4px 0' };
  const code = { fontSize: 12, color: 'var(--text-4)' };
  const dates = { fontSize: 12, color: 'var(--text-3)', textAlign: 'right', whiteSpace: 'nowrap' };
  const moved = [];
  if (now.start !== was.start) moved.push(`start moves to ${fmt(now.start)}`);
  if (now.finish !== was.finish) moved.push(`finish moves to ${fmt(now.finish)}`);
  const more = (n, where) => n > 0 && (
    <div style={{ ...grid, color: 'var(--text-4)', fontSize: 12.5 }}><span /><span /><span style={{ paddingLeft: 18 }}>… {n} more {where}</span><span /></div>
  );
  return (
    <div style={{ marginTop: 10, border: '1px solid color-mix(in oklch, var(--accent) 40%, transparent)', background: 'color-mix(in oklch, var(--accent) 6%, transparent)', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 6h11" /><path d="M12 12h8" /><path d="M15 18h5" /><path d="M5 6v4a2 2 0 0 0 2 2h2" /><path d="M10 12v4a2 2 0 0 0 2 2h1" /></svg>
        Where it goes on the schedule
      </div>
      <div style={{ ...grid, fontWeight: 600, color: 'var(--text-1)' }}>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>▼</span>
        <span className="mono" style={code}>{parent.task_code}</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{parent.task_name} <span className="mono" style={{ color: 'var(--text-3)', fontWeight: 500, fontSize: 12.5 }}>{rows.length}</span></span>
        <span className="mono" style={dates}>
          <span style={{ color: now.start !== was.start ? 'var(--accent-text)' : undefined }}>{fmt(now.start)}</span> → <span style={{ color: now.finish !== was.finish ? 'var(--accent-text)' : undefined }}>{fmt(now.finish)}</span>
        </span>
      </div>
      {more(from, 'above')}
      {rows.slice(from, to).map((r, i) => (r._me ? (
        <div key="me" style={{ ...grid, alignItems: notes.length ? 'start' : 'center', background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--accent) 45%, transparent)', borderRadius: 6, margin: '2px -6px', padding: '4px 6px' }}>
          <span />
          <span className="mono" style={{ ...code, color: 'var(--accent-text)', paddingLeft: 12, lineHeight: '20px' }}>.{from + i + 1}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', minWidth: 0, fontWeight: 600, color: 'var(--text-1)', lineHeight: '20px' }}>
              {elbow('color-mix(in oklch, var(--accent) 60%, transparent)')}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.task_name || 'New subtask'}</span>
              <span style={{ marginLeft: 6, fontSize: 11.5, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-soft)', borderRadius: 999, padding: '0 7px', flexShrink: 0 }}>{meLabel}</span>
            </span>
            {notes.map((n) => (
              <span key={n.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 5, paddingLeft: 18, marginTop: 2, fontSize: 12.5, fontWeight: 500, lineHeight: 1.35, color: NOTE_COLOR[n.tone] }}>
                <span style={{ flexShrink: 0, marginTop: 1 }}>{NOTE_ICON[n.tone]}</span>
                <span>{n.text}</span>
              </span>
            ))}
          </span>
          <span className="mono" style={{ ...dates, lineHeight: '20px' }}>{r.start_date ? `${fmt(r.start_date)} → ${fmt(r.finish_date || r.start_date)}` : 'pick its dates'}</span>
        </div>
      ) : (
        <div key={r.id} style={grid}>
          <span />
          <span className="mono" style={{ ...code, paddingLeft: 12 }}>.{from + i + 1}</span>
          <span style={{ display: 'flex', alignItems: 'center', minWidth: 0, color: 'var(--text-2)' }}>{elbow('var(--border-strong, #cbd5e1)')}<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.task_name}</span></span>
          <span className="mono" style={dates}>{fmt(r.start_date)} → {fmt(r.finish_date || r.start_date)}</span>
        </div>
      )))}
      {more(rows.length - to, 'below')}
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
        {others.length ? '' : `${parent.task_name} gets its first subtask. `}
        {moved.length ? `${parent.task_name}'s ${moved.join(' and its ')}. ` : ''}
        Its dates and % come from its subtasks.
      </div>
      {offers.length > 0 && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid color-mix(in oklch, var(--accent) 25%, transparent)', display: 'grid', gap: 6 }}>
          {offers.map((o) => (
            <label key={o.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-1)', cursor: 'pointer', lineHeight: 1.4 }}>
              <input type="checkbox" checked={picks[o.key] !== false} onChange={(e) => onPick && onPick(o.key, e.target.checked)} style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
              <span>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--accent-text)', marginRight: 6 }}>{o.kind === 'wait' ? 'Wait too?' : 'Move the arrow?'}</span>
                {o.text}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
